import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { userLsKey } from "@/lib/userScopedStorage";
import { useTodayDate } from "@/hooks/useTodayDate";
import { Trophy, Award, Star, Target, CheckSquare, Calendar, FileText, Zap, Settings } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import type { Goal, Activity, DashboardStats, HabitStats, JournalStats, CalendarStats } from "@shared/schema";
import { isFutureGoal } from "@/lib/goal-visibility";

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  category: "goals" | "habits" | "journal" | "calendar" | "general";
  isUnlocked: boolean;
  progress: number;
  maxProgress: number;
  unlockedAt?: Date;
  tier: "bronze" | "silver" | "gold" | "platinum";
}

export default function AchievementsPage() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [visibilitySettings, setVisibilitySettings] = useState({
    goals: true,
    habits: true,
    journal: true,
    calendar: true,
    general: true,
  });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Load visibility settings from user-scoped localStorage key.
  useEffect(() => {
    const saved = localStorage.getItem(userLsKey("achievement-visibility", userId));
    if (saved) {
      try {
        setVisibilitySettings(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to parse achievement visibility settings", e);
      }
    }
  }, [userId]);

  // Save visibility settings to user-scoped localStorage key.
  const updateVisibilitySetting = (category: keyof typeof visibilitySettings, value: boolean) => {
    const newSettings = { ...visibilitySettings, [category]: value };
    setVisibilitySettings(newSettings);
    localStorage.setItem(userLsKey("achievement-visibility", userId), JSON.stringify(newSettings));
  };

  const { data: allGoals = [] } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });
  const goals = allGoals.filter(goal => !isFutureGoal(goal));

  const { data: activities = [] } = useQuery<Activity[]>({
    queryKey: ["/api/activities"],
  });

  const { data: dashboardStats } = useQuery<DashboardStats>({
    queryKey: ["/api/stats/dashboard"],
  });

  const localDate = useTodayDate();
  const { data: habitStats } = useQuery<HabitStats>({
    queryKey: ["/api/stats/habits", localDate],
    queryFn: () =>
      fetch(`/api/stats/habits?date=${localDate}`, { credentials: "include", cache: "no-store" }).then(r => r.json()),
  });

  const { data: journalStats } = useQuery<JournalStats>({
    queryKey: ["/api/stats/journal"],
  });

  const { data: calendarStats } = useQuery<CalendarStats>({
    queryKey: ["/api/stats/calendar"],
  });

  // Generate achievements based on user data
  const achievements: Achievement[] = [
    // Goal Achievements
    {
      id: "first-goal",
      title: "Goal Setter",
      description: "Create your first goal",
      icon: <Target className="h-6 w-6" />,
      category: "goals",
      isUnlocked: goals.length > 0,
      progress: Math.min(goals.length, 1),
      maxProgress: 1,
      tier: "bronze",
      unlockedAt: goals.length > 0 ? new Date(goals[0].createdAt) : undefined,
    },
    {
      id: "goal-completer",
      title: "Goal Achiever",
      description: "Complete your first goal",
      icon: <CheckSquare className="h-6 w-6" />,
      category: "goals",
      isUnlocked: goals.some(g => g.status === "completed"),
      progress: goals.filter(g => g.status === "completed").length,
      maxProgress: 1,
      tier: "silver",
    },
    {
      id: "goal-master",
      title: "Goal Master",
      description: "Complete 10 goals",
      icon: <Trophy className="h-6 w-6" />,
      category: "goals",
      isUnlocked: goals.filter(g => g.status === "completed").length >= 10,
      progress: goals.filter(g => g.status === "completed").length,
      maxProgress: 10,
      tier: "gold",
    },
    
    // Habit Achievements
    {
      id: "habit-starter",
      title: "Habit Builder",
      description: "Start tracking habits",
      icon: <Zap className="h-6 w-6" />,
      category: "habits",
      isUnlocked: (habitStats?.totalHabits || 0) > 0,
      progress: Math.min(habitStats?.totalHabits || 0, 1),
      maxProgress: 1,
      tier: "bronze",
    },
    {
      id: "streak-master",
      title: "Streak Master",
      description: "Maintain a 30-day habit streak",
      icon: <Star className="h-6 w-6" />,
      category: "habits",
      isUnlocked: (habitStats?.longestStreak || 0) >= 30,
      progress: Math.min(habitStats?.longestStreak || 0, 30),
      maxProgress: 30,
      tier: "gold",
    },
    
    // Journal Achievements
    {
      id: "first-entry",
      title: "Journal Keeper",
      description: "Write your first journal entry",
      icon: <FileText className="h-6 w-6" />,
      category: "journal",
      isUnlocked: (journalStats?.totalEntries || 0) > 0,
      progress: Math.min(journalStats?.totalEntries || 0, 1),
      maxProgress: 1,
      tier: "bronze",
    },
    {
      id: "prolific-writer",
      title: "Prolific Writer",
      description: "Write 50 journal entries",
      icon: <Award className="h-6 w-6" />,
      category: "journal",
      isUnlocked: (journalStats?.totalEntries || 0) >= 50,
      progress: Math.min(journalStats?.totalEntries || 0, 50),
      maxProgress: 50,
      tier: "platinum",
    },
    
    // Calendar Achievements
    {
      id: "scheduler",
      title: "Event Scheduler",
      description: "Schedule your first event",
      icon: <Calendar className="h-6 w-6" />,
      category: "calendar",
      isUnlocked: (calendarStats?.totalEvents || 0) > 0,
      progress: Math.min(calendarStats?.totalEvents || 0, 1),
      maxProgress: 1,
      tier: "bronze",
    },
  ];

  // Filter achievements based on visibility settings
  const visibleAchievements = achievements.filter(a => visibilitySettings[a.category]);
  const unlockedAchievements = visibleAchievements.filter(a => a.isUnlocked);
  const lockedAchievements = visibleAchievements.filter(a => !a.isUnlocked);

  const getTierColor = (tier: Achievement["tier"]) => {
    switch (tier) {
      case "bronze": return "text-orange-600 bg-orange-100 dark:bg-orange-900 dark:text-orange-100";
      case "silver": return "text-gray-600 bg-gray-100 dark:bg-gray-900 dark:text-gray-100";
      case "gold": return "text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-100";
      case "platinum": return "text-purple-600 bg-purple-100 dark:bg-purple-900 dark:text-purple-100";
    }
  };

  const getCategoryColor = (category: Achievement["category"]) => {
    switch (category) {
      case "goals": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100";
      case "habits": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100";
      case "journal": return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100";
      case "calendar": return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100";
      case "general": return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100";
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      <div className="flex flex-col flex-1">
        <main className="flex-1 pb-8">
          {/* Header */}
          <div className="bg-card border-b border-border px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                  <Trophy className="h-6 w-6" />
                  Achievements
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground">Track your progress and celebrate your wins</p>
              </div>
              
              <div className="flex items-center gap-4">
                <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="hidden sm:inline-flex" data-testid="button-achievement-settings">
                      <Settings className="h-4 w-4 mr-2" />
                      Settings
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Achievement Display Settings</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <p className="text-sm text-muted-foreground mb-4">
                        Choose which achievement categories to display
                      </p>
                      
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="goals-toggle" className="flex items-center gap-2">
                            <Target className="h-4 w-4 text-blue-600" />
                            Goal Achievements
                          </Label>
                          <Switch
                            id="goals-toggle"
                            checked={visibilitySettings.goals}
                            onCheckedChange={(checked) => updateVisibilitySetting("goals", checked)}
                            data-testid="toggle-goals-achievements"
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <Label htmlFor="habits-toggle" className="flex items-center gap-2">
                            <Zap className="h-4 w-4 text-green-600" />
                            Habit Achievements
                          </Label>
                          <Switch
                            id="habits-toggle"
                            checked={visibilitySettings.habits}
                            onCheckedChange={(checked) => updateVisibilitySetting("habits", checked)}
                            data-testid="toggle-habits-achievements"
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <Label htmlFor="journal-toggle" className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-purple-600" />
                            Journal Achievements
                          </Label>
                          <Switch
                            id="journal-toggle"
                            checked={visibilitySettings.journal}
                            onCheckedChange={(checked) => updateVisibilitySetting("journal", checked)}
                            data-testid="toggle-journal-achievements"
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <Label htmlFor="calendar-toggle" className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-orange-600" />
                            Calendar Achievements
                          </Label>
                          <Switch
                            id="calendar-toggle"
                            checked={visibilitySettings.calendar}
                            onCheckedChange={(checked) => updateVisibilitySetting("calendar", checked)}
                            data-testid="toggle-calendar-achievements"
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <Label htmlFor="general-toggle" className="flex items-center gap-2">
                            <Trophy className="h-4 w-4 text-gray-600" />
                            General Achievements
                          </Label>
                          <Switch
                            id="general-toggle"
                            checked={visibilitySettings.general}
                            onCheckedChange={(checked) => updateVisibilitySetting("general", checked)}
                            data-testid="toggle-general-achievements"
                          />
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{unlockedAchievements.length}</div>
                  <div className="text-xs text-muted-foreground">Unlocked</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-muted-foreground">{visibleAchievements.length}</div>
                  <div className="text-xs text-muted-foreground">Visible</div>
                </div>
              </div>
            </div>
          </div>

          {/* Achievements Content */}
          <div className="px-4 sm:px-6 lg:px-8 py-6">
          {/* Progress Overview */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Achievement Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1">
                  <Progress 
                    value={visibleAchievements.length > 0 ? (unlockedAchievements.length / visibleAchievements.length) * 100 : 0} 
                    className="h-3"
                  />
                </div>
                <div className="text-sm font-medium">
                  {visibleAchievements.length > 0 ? Math.round((unlockedAchievements.length / visibleAchievements.length) * 100) : 0}%
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-lg font-bold text-orange-600">
                    {unlockedAchievements.filter(a => a.tier === "bronze").length}
                  </div>
                  <div className="text-xs text-muted-foreground">Bronze</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-gray-600">
                    {unlockedAchievements.filter(a => a.tier === "silver").length}
                  </div>
                  <div className="text-xs text-muted-foreground">Silver</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-yellow-600">
                    {unlockedAchievements.filter(a => a.tier === "gold").length}
                  </div>
                  <div className="text-xs text-muted-foreground">Gold</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-purple-600">
                    {unlockedAchievements.filter(a => a.tier === "platinum").length}
                  </div>
                  <div className="text-xs text-muted-foreground">Platinum</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Unlocked Achievements */}
          {unlockedAchievements.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Award className="h-5 w-5" />
                Unlocked Achievements ({unlockedAchievements.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {unlockedAchievements.map((achievement) => (
                  <Card key={achievement.id} className="relative overflow-hidden border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg text-primary">
                          {achievement.icon}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold">{achievement.title}</h3>
                            <Badge className={getTierColor(achievement.tier)} variant="outline">
                              {achievement.tier}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {achievement.description}
                          </p>
                          <div className="flex items-center justify-between">
                            <Badge className={getCategoryColor(achievement.category)} variant="outline">
                              {achievement.category}
                            </Badge>
                            {achievement.unlockedAt && (
                              <div className="text-xs text-muted-foreground">
                                {new Date(achievement.unlockedAt).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Locked Achievements */}
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Target className="h-5 w-5" />
              In Progress ({lockedAchievements.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {lockedAchievements.map((achievement) => (
                <Card key={achievement.id} className="opacity-75 hover:opacity-90 transition-opacity">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-muted rounded-lg text-muted-foreground">
                        {achievement.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-muted-foreground">{achievement.title}</h3>
                          <Badge className={getTierColor(achievement.tier)} variant="outline">
                            {achievement.tier}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">
                          {achievement.description}
                        </p>
                        
                        {/* Progress Bar */}
                        <div className="mb-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-muted-foreground">Progress</span>
                            <span className="text-xs text-muted-foreground">
                              {achievement.progress}/{achievement.maxProgress}
                            </span>
                          </div>
                          <Progress 
                            value={(achievement.progress / achievement.maxProgress) * 100} 
                            className="h-2"
                          />
                        </div>
                        
                        <Badge className={getCategoryColor(achievement.category)} variant="outline">
                          {achievement.category}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Achievement Stats */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Achievement Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary">
                    {visibleAchievements.length > 0 ? Math.round((unlockedAchievements.length / visibleAchievements.length) * 100) : 0}%
                  </div>
                  <div className="text-sm text-muted-foreground">Completion Rate</div>
                </div>
                
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {unlockedAchievements.filter(a => a.tier === "gold" || a.tier === "platinum").length}
                  </div>
                  <div className="text-sm text-muted-foreground">Rare Achievements</div>
                </div>
                
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {goals.filter(g => g.status === "active").length}
                  </div>
                  <div className="text-sm text-muted-foreground">Active Goals</div>
                </div>
                
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {activities.length}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Activities</div>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        </main>
      </div>
    </div>
  );
}